import type {
  Display,
  Price,
  PriceRange,
  ReductionHistory,
  ReductionHistoryRange,
} from "@/types/prodcatBaseTypes";
import type { SimpleProductVariantGroup } from "@/types/types.ts";

/**
 * Checks if a price string represents a whole number (no fractional pence/cents).
 *
 * Matches the zero-decimal criteria:
 * - Plain integers (e.g., 25)
 * - Standard double-zero decimals (e.g., 25.00)
 * - Single-zero decimals (e.g., 25.0)
 * - Trailing decimal notations (e.g., 25.)
 *
 * @param priceString - The price string to evaluate (may contain currency symbols, commas)
 * @returns true if the price is a whole number, false otherwise
 */
const isWholeNumberPrice = (priceString: string | undefined): boolean => {
  if (!priceString) {
    return false;
  }

  // Extract numeric part: remove currency symbols, spaces, commas
  const numericPart = priceString.replace(/[^\d.]/g, "");

  if (!numericPart) {
    return false;
  }

  // Convert to number and check if it's a whole number
  const price = parseFloat(numericPart);

  if (Number.isNaN(price)) {
    return false;
  }

  // Check if the number is a whole number (no fractional part)
  return price % 1 === 0;
};

const isDisplayRange = (
  display: string | Display | undefined,
): display is Display => typeof display === "object" && display !== null;

const hasWholeNumberDisplay = (
  display: string | Display | undefined,
): boolean =>
  isDisplayRange(display)
    ? isWholeNumberPrice(display.min) || isWholeNumberPrice(display.max)
    : isWholeNumberPrice(display);

const hasWholeNumberReductionHistory = (
  reductionHistory: Array<ReductionHistory | ReductionHistoryRange> | undefined,
): boolean =>
  reductionHistory?.some((item) => hasWholeNumberDisplay(item.display)) ??
  false;

/**
 * Checks if a product or variant has any whole-number prices in either:
 * 1. The active/current purchase price, OR
 * 2. Any historical price reduction entries
 *
 * Returns true if ANY price qualifies as a whole number.
 * Handles Price, PriceRange, and VariantGroupPriceRange types.
 *
 * @param priceData - Price-like data used by the Price component
 * @returns true if any price is a whole number, false otherwise
 */
export const hasWholeNumberPrice = (
  priceData:
    | Price
    | PriceRange
    | SimpleProductVariantGroup["variantPriceRange"]
    | undefined,
): boolean => {
  if (!priceData) {
    return false;
  }

  if (hasWholeNumberDisplay(priceData.display)) {
    return true;
  }

  return hasWholeNumberReductionHistory(priceData.reductionHistory);
};
